/**
 * Wishlist functionality using localStorage
 * Manages wishlist items and provides event system for UI updates
 */

class WishlistManager {
  constructor() {
    this.storageKey = "engel_wishlist";
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    // Emit initial wishlist count
    this.emitWishlistUpdate();

    // Listen for product card heart clicks
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-wishlist-toggle]")) {
        e.preventDefault();
        const button = e.target.closest("[data-wishlist-toggle]");
        const productId = button.dataset.productId;
        const productData = this.getProductDataFromButton(button);

        if (productId) {
          this.toggleWishlistItem(productId, productData);
        }
      }
    });
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error loading wishlist:", error);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.emitWishlistUpdate();
    } catch (error) {
      console.error("Error saving wishlist:", error);
    }
  }

  getProductDataFromButton(button) {
    console.log("getProductDataFromButton called with button:", button);
    console.log("Button dataset:", button.dataset);

    // Extract product data from the button's data attributes or parent elements
    const productCard = button.closest("[data-product-card]");
    console.log("Product card found:", productCard);

    if (!productCard) {
      console.error("No product card found!");
      return null;
    }

    const productData = {
      id: button.dataset.productId,
      variantId: button.dataset.variantId,
      title:
        productCard
          .querySelector("[data-product-title]")
          ?.textContent?.trim() || "",
      price:
        productCard
          .querySelector("[data-product-price]")
          ?.textContent?.trim() || "",
      compareAtPrice:
        productCard
          .querySelector("[data-product-compare-price]")
          ?.textContent?.trim() || "",
      image: productCard.querySelector("[data-product-image]")?.src || "",
      url: productCard.querySelector("a")?.href || "",
    };

    console.log("Extracted product data:", productData);
    return productData;
  }

  addToWishlist(productId, productData) {
    if (!productId) return false;

    // Check if already in wishlist
    const existingIndex = this.wishlist.findIndex(
      (item) => item.id === productId,
    );
    if (existingIndex !== -1) {
      return false; // Already exists
    }

    this.wishlist.push({
      id: productId,
      ...productData,
      addedAt: new Date().toISOString(),
    });

    this.saveWishlist();
    return true;
  }

  removeFromWishlist(productId) {
    const initialLength = this.wishlist.length;
    this.wishlist = this.wishlist.filter((item) => item.id !== productId);

    if (this.wishlist.length !== initialLength) {
      this.saveWishlist();
      return true;
    }
    return false;
  }

  toggleWishlistItem(productId, productData) {
    const isInWishlist = this.isInWishlist(productId);

    if (isInWishlist) {
      this.removeFromWishlist(productId);
      return false; // Removed
    } else {
      this.addToWishlist(productId, productData);
      return true; // Added
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some((item) => item.id === productId);
  }

  getWishlist() {
    return [...this.wishlist]; // Return copy
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
  }

  async moveToCart(productId) {
    console.log("moveToCart called for productId:", productId);

    // This will integrate with the existing cart functionality
    const item = this.wishlist.find((item) => item.id === productId);
    console.log("Found wishlist item:", item);

    if (!item) {
      console.error("No wishlist item found for productId:", productId);
      return false;
    }

    try {
      // Get the first available variant ID from the product
      const variantId = item.variantId || this.getVariantIdFromProduct(item);
      console.log("Variant ID:", variantId);

      if (!variantId) {
        console.error("No variant ID found for product. Item data:", item);
        return false;
      }

      console.log("Calling cartManager.addToCart with variantId:", variantId);

      // Use the cart manager to add to cart
      if (window.cartManager) {
        // Create a mock button element with the required data attributes
        const mockButton = {
          dataset: {
            variantId: variantId,
            quantity: 1,
          },
          disabled: false,
          style: {},
        };

        await window.cartManager.addToCart(mockButton);

        // Remove from wishlist after successful cart addition
        this.removeFromWishlist(productId);
        return true;
      } else {
        console.error("Cart manager not found");
        return false;
      }
    } catch (error) {
      console.error("Error moving to cart:", error);
    }

    return false;
  }

  // Helper function to get variant ID from product data
  getVariantIdFromProduct(item) {
    // Try to extract variant ID from the product URL or other data
    // This is a fallback - ideally the variant ID should be stored when adding to wishlist
    if (item.url) {
      // Extract variant ID from URL if available
      const urlParams = new URLSearchParams(item.url.split("?")[1]);
      return urlParams.get("variant");
    }
    return null;
  }

  emitWishlistUpdate() {
    const event = new CustomEvent("wishlist:updated", {
      detail: {
        wishlist: this.getWishlist(),
        count: this.getWishlistCount(),
      },
    });
    window.dispatchEvent(event);
  }

  // Format money helper (same as cart)
  formatMoney(cents) {
    return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
  }
}

// Initialize wishlist manager
window.WishlistManager = new WishlistManager();

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = WishlistManager;
}
